/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   location utilities
 */

import { boxToQueryBounds } from '@zcode/nr-utils'
import { isNumber } from '@zcode/utils'
import { chomp, chomps } from '../index'
import * as Utils from '../utils'
import getBestHood from './getBestHood'
import getBoxJS from './getBoxJS'
import getClosestEntity from './getClosestEntity'
import getDistanceFromCenter from './getDistanceFromCenter'
import getMatchingEntity from './getMatchingEntity'
import withinBox from './withinBox'

// TODO(TAE): move these constants to a const file once we figure out how to do file structure
export const STREET_ADDRESS = 'street_address'
export const NEIGHBORHOOD = 'neighborhood'
export const LOCALITY = 'locality'
export const ROUTE = 'route'
export const BOX_PARAMETER_ORDER = ['minLng', 'minLat', 'maxLng', 'maxLat']

/**
 * `minLat,minLng,maxLat,maxLng` eg. `sw.lng,sw.lat,ne.lng,ne.lat`
 * aka the 'box' parameter
 *
 * @param bounds
 * @param precision
 * @returns {string}
 */
export const boundsToString = (bounds, precision = 10) => {
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()
  const box = [
    chomp(sw.lng(), precision),
    chomp(sw.lat(), precision),
    chomp(ne.lng(), precision),
    chomp(ne.lat(), precision),
  ]
  return box.map((n) => chomps(n, precision)).join(',')
}

export const getBoxFromHoodOrCity = (opt, opt2) => {
  if (opt && opt.box) {
    return boxToQueryBounds(opt.box)
  } else if (opt2 && opt2.box) {
    return boxToQueryBounds(opt2.box)
  }
  return undefined
}

export const boxesIntersect = (box1, box2) => {
  if (!box1 || !box2) {
    return undefined
  }
  if (Array.isArray(box1)) {
    box1 = boxToQueryBounds(box1)
  }
  if (Array.isArray(box2)) {
    box2 = boxToQueryBounds(box2)
  }

  // TODO: ensure this works with the equator
  if (box1.minLat > box2.maxLat || box2.minLat > box1.maxLat) {
    return false
  }
  if (box1.minLng > box2.maxLng || box2.minLng > box1.maxLng) {
    return false
  }
  return true
}

// Attempts to create a usable "size" for a box to help compare boxes
export const getBoxRadius = (box) => {
  if (Array.isArray(box)) {
    box = boxToQueryBounds(box)
  }
  const { maxLat, maxLng, minLat, minLng } = box
  const side1 = Utils.distance({ lat1: minLat, lng1: minLng, lat2: maxLat, lng2: minLng })
  const side2 = Utils.distance({ lat1: minLat, lng1: minLng, lat2: minLat, lng2: maxLng })
  // average of the two measurments, cut in half
  return (side1 + side2) / 4
}

/**
 *
 * @param {{ lat: number | null; lng: number | null }} center
 * @param {import('@zcode/state-type').MainMapMetaTypes['box'] | null} box
 * @param {import('@zcode/state-type').Geo} geos
 */
export const getBestCity = (center, box, geos) => {
  const hasCity = !!geos.city
  const hasCities = geos.cities && Array.isArray(geos.cities) && geos.cities.length
  const hasNearbyCities =
    geos.nearbyCities && Array.isArray(geos.nearbyCities) && geos.nearbyCities.length
  let cities = hasCity ? [geos.city] : []
  if (hasCities && hasNearbyCities) {
    cities = cities.concat(geos.cities, geos.nearbyCities)
  } else if (hasNearbyCities) {
    cities = cities.concat(geos.nearbyCities)
  }
  const boxRadius = getBoxRadius(box)
  let bestCity
  let bestScore = 0
  for (const city of cities) {
    if (!boxesIntersect(city.box, box)) {
      continue
    }
    const radius = getBoxRadius(city.box)
    const distance = getDistanceFromCenter(center, city)
    const ratio = distance / radius
    const closeness = 1 / ratio
    const smallness = 1 / (radius / boxRadius)
    if (smallness > 10) {
      // skip cities that are too small
      continue
    }
    const boxBoost = Math.max(withinBox(center, city.box) ? 1000 * smallness : 1, 1)
    const radiusBoost = Math.max(distance < radius ? 10 * smallness : 1, 1)
    const score = closeness * boxBoost * radiusBoost
    if (score > bestScore) {
      bestScore = score
      bestCity = city
    }
  }
  return bestCity
}

/**
 * extract the center in object form from a LatLngBounds
 *
 * @param {LatLngBounds} bounds
 * @returns {{lat: number, lng: number}}
 */
export const getGBoundsCenter = (bounds) => {
  const center = bounds.getCenter()
  return { lat: center.lat(), lng: center.lng() }
}

export const getYDistanceFromCenter = (center, entity) => {
  const { lat } = entity

  if (!isNumber(lat) || !center || !isNumber(center.lat)) {
    return undefined
  }
  const distance = Utils.distance({
    lat1: lat,
    lng1: center.lng,
    lat2: center.lat,
    lng2: center.lng,
  })
  return distance
}

export const getXDistanceFromCenter = (center, entity) => {
  const { lng } = entity

  if (!isNumber(lng) || !center || !isNumber(center.lng)) {
    return undefined
  }
  const distance = Utils.distance({
    lat1: center.lat,
    lng1: lng,
    lat2: center.lat,
    lng2: center.lng,
  })
  return distance
}

/**
 *
 * @param {import('@zcode/state-type').Location} location
 * @returns {location is import('@zcode/state-type').Location & {readonly box: {[Key in keyof NonNullable<import('@zcode/state-type').Location['box']>]: NonNullable<NonNullable<import('@zcode/state-type').Location['box']>[Key]>}}}
 */
export const isBoxSearch = (location) => {
  return !!location?.box && Object.values(location.box).every(isNumber)
}

export const eqBox = (boxA, boxB) => {
  if (boxA && boxB) {
    if (typeof boxA === 'string' && typeof boxB === 'string') {
      return boxA === boxB
    }
    return boxA.equals(boxB)
  }
  return false
}

export {
  boxToQueryBounds,
  getBestHood,
  getBoxJS,
  getClosestEntity,
  getMatchingEntity,
  getDistanceFromCenter,
  withinBox,
}
